import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { ConfirmationService } from 'primeng/api';
import { CommonService } from 'wire-frame';

import { ToasterService } from '../../../service/common/toaster.service';
import {
  FamilyCardMatch,
  PendingRegistration,
  PendingRegistrationService,
} from '../../../service/admin/pending-registration.service';
import { formatPatientAge } from '../../../utils/patient-age.util';

@Component({
  selector: 'app-pending-registrations',
  standalone: true,
  imports: [CommonModule, FormsModule, ToastModule, ConfirmDialogModule, DialogModule, SkeletonModule],
  providers: [ConfirmationService, PendingRegistrationService],
  templateUrl: './pending-registrations.component.html',
  styleUrl: './pending-registrations.component.scss',
})
export class PendingRegistrationsComponent implements OnInit, OnDestroy {
  searchSubject: Subject<string> = new Subject<string>();
  private searchSubscription!: Subscription;

  records: PendingRegistration[] = [];
  pendingCount = 0;
  loading = false;
  processingId: number | null = null;
  
  currentPage = 1;
  pageSize = 15;
  totalRecords = 0;
  totalPages = 1;

  get visiblePageNumbers(): number[] {
    const radius = 2;
    const start = Math.max(1, this.currentPage - radius);
    const end = Math.min(this.totalPages, this.currentPage + radius);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.load();
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.load();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.load();
    }
  }

  onPageSizeChanged(): void {
    this.currentPage = 1;
    this.load();
  }

  statusFilter: 'pending' | 'approved' | 'rejected' | 'all' = 'pending';
  searchTerm = '';

  // Approval dialog state. selectedCardId === null means "create a new card".
  showApproveDialog = false;
  approvingRecord: PendingRegistration | null = null;
  selectedCardId: number | null = null;

  readonly statusOptions: { label: string; value: 'pending' | 'approved' | 'rejected' | 'all' }[] = [
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'All', value: 'all' }];

  constructor(
    public common: CommonService,
    private service: PendingRegistrationService,
    private toaster: ToasterService, private confirmationService: ConfirmationService
  ) {
    this.common.breadCrum = {
      title: 'Self Registrations',
      icon: 'user',
      list: ['Self Registrations'],
    };
  }

  ngOnInit(): void {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(800),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1;
      this.load();
    });
    this.load();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  load(): void {
    this.loading = true;
    this.service.list(this.statusFilter, this.searchTerm.trim(), this.currentPage, this.pageSize).subscribe({
      next: (res) => {
        this.records = res?.data || [];
        this.totalRecords = res?.pagination?.total || 0;
        this.totalPages = res?.pagination?.last_page || 1;
        this.currentPage = res?.pagination?.current_page || 1;
        this.pendingCount = res?.pending_count ?? 0;
        this.loading = false;
      },
      error: () => {
        this.records = [];
        this.totalRecords = 0;
        this.totalPages = 1;
        this.loading = false;
        this.toaster.error('Could not load registrations.');
      },
    });
  }

  setStatus(value: 'pending' | 'approved' | 'rejected' | 'all'): void {
    this.statusFilter = value;
    this.currentPage = 1;
    this.load();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  approve(record: PendingRegistration): void {
    this.approvingRecord = record;
    // Default to creating a new family card. If exactly one family match
    // exists we still leave the choice to the reviewer, but the suggestion
    // is right there in the dialog.
    this.selectedCardId = null;
    this.showApproveDialog = true;
  }

  get familyMatches(): FamilyCardMatch[] {
    return this.approvingRecord?.family_matches || [];
  }

  selectCard(cardId: number | null): void {
    this.selectedCardId = cardId;
  }

  confirmApprove(): void {
    const record = this.approvingRecord;
    if (!record) return;

    const overrides: Record<string, any> = {};
    if (this.selectedCardId) {
      overrides['card_id'] = this.selectedCardId;
    }

    this.processingId = record.id;
    this.showApproveDialog = false;

    this.service.approve(record.id, overrides).subscribe({
      next: (res) => {
        this.processingId = null;
        const number = res?.data?.patient_number;
        this.toaster.add({
          severity: 'success',
          summary: 'Approved',
          detail: number
            ? `${record.full_name} added as patient ${number}.`
            : `${record.full_name} added.`,
        });
        this.approvingRecord = null;
        this.load();
      },
      error: (err) => {
        this.processingId = null;
        this.toaster.error(err?.error?.message || 'Please try again.', 'Could not approve');
      },
    });
  }

  reject(record: PendingRegistration): void {
    this.confirmationService.confirm({
      header: 'Reject registration',
      message: `Reject the registration from ${record.full_name}? This cannot be undone.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Reject',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.processingId = record.id;
        this.service.reject(record.id).subscribe({
          next: () => {
            this.processingId = null;
            this.toaster.add({
              severity: 'info',
              summary: 'Rejected',
              detail: `Registration from ${record.full_name} was rejected.`,
            });
            this.load();
          },
          error: (err) => {
            this.processingId = null;
            this.toaster.error(err?.error?.message || 'Please try again.', 'Could not reject');
          },
        });
      },
    });
  }

  initials(name: string | null | undefined): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }

  statusClass(status: string): string {
    switch (status) {
      case 'pending':
        return 'badge-pending';
      case 'approved':
        return 'badge-approved';
      case 'rejected':
        return 'badge-rejected';
      default:
        return 'badge-default';
    }
  }

  displayAge(record: PendingRegistration): string {
    return formatPatientAge(record.age, record.age_months, record.age_days);
  }
}
